import React from 'react';

import { useGoogleDrive } from 'api/google/useGoogleDrive';

import {
  Stack,
  Menu,
  MenuItem,
  IconButton,
  Actions,
  Action,
  AssignmentIcon,
  DescriptionIcon,
} from '@gogame/components';
import { useChooseFiles } from '@gogame/hooks-ts';

import {
  useExportFromGoogleDrive,
  useImportFromGooglePicker,
  useImportCSV,
  useExportCSV,
} from 'scenes/MarketingPlanning/MarketingPlanningGrid/data';

import ErrorLog from 'scenes/MarketingPlanning/MarketingPlanningGrid/components/ErrorLog';

const MarketingPlanningActions = ({
  onShowSummaryByOwners,
  onShowSummaryByManagers,
  onShowSummaryByDirectors,
  onShowHistory,
  onSuccess,
  onHasErrors,
  data,
  onOpenChildComponent,
}) => {
  const chooseFiles = useChooseFiles({ accept: '.csv' });

  const [importFromGD] = useImportFromGooglePicker({
    onSuccess: onSuccess,
    onHasErrors: onHasErrors,
  });
  const [exportFromGD] = useExportFromGoogleDrive();
  const [exportCSV] = useExportCSV();
  const [importCSV] = useImportCSV({
    onSuccess: onSuccess,
    onHasErrors: onHasErrors,
  });

  const { year, month, hasImportErrors } = data;

  const handleImportCSV = async () => {
    const [file] = await chooseFiles();
    const fileInFormData = new FormData();
    fileInFormData.append(
      'file',
      new Blob([file], { type: 'application/octet-stream' }),
      file.name
    );

    await importCSV({ year, month, file: fileInFormData });
  };

  const handleSelectFile = ({ docs, token, year, month }) => {
    docs.forEach(async ({ id }) => {
      await importFromGD({ fileId: id, accessToken: token, year, month });
    });
  };

  const handleExport = ({ year, month, token }) => {
    exportFromGD({ year, month, accessToken: token });
  };

  const handleExportCSV = async () => {
    await exportCSV({ year, month });
  };

  const { onImport, onExport } = useGoogleDrive({
    onSelectFiles: handleSelectFile,
    onGetToken: handleExport,
  });

  const handleSummaryByOwners = () => onShowSummaryByOwners(data);
  const handleSummaryByManagers = () => onShowSummaryByManagers(data);
  const handleSummaryByDirectors = () => onShowSummaryByDirectors(data);
  const handleShowHistory = () => onShowHistory(data);

  const handleErrorLog = () => onOpenChildComponent(ErrorLog, { year, month });

  return (
    <Stack direction={'row'}>
      <Menu
        anchor={
          <IconButton
            icon={<AssignmentIcon />}
            style={{ margin: '0 8px 0 0' }}
            tooltip={'View summary'}
          />
        }
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleSummaryByOwners}>Summary by owners</MenuItem>
        <MenuItem onClick={handleSummaryByManagers}>
          Summary by managers
        </MenuItem>
        <MenuItem onClick={handleSummaryByDirectors}>
          Summary by directors
        </MenuItem>
      </Menu>
      <Actions limit={2}>
        <Action
          id={'history'}
          icon={<DescriptionIcon />}
          label={'View history'}
          onClick={handleShowHistory}
        />
        <Action id={'import'} label={'Import'} onClick={handleImportCSV} />
        <Action
          id={'importGD'}
          label={'Import from Google Drive'}
          onClick={onImport(data)}
        />
        <Action id={'export'} label={'Export'} onClick={handleExportCSV} />
        <Action
          id={'exportGD'}
          label={'Export to Google Drive'}
          onClick={onExport(data)}
        />
        {hasImportErrors ? (
          <Action label={'Error Log'} onClick={handleErrorLog} />
        ) : null}
      </Actions>
    </Stack>
  );
};

export default MarketingPlanningActions;
